/**
 * MLC Proposal HQ - Calendar & Milestone Utilities
 * Backward-scheduling calculations, countdown timers, ICS & Google Calendar exports.
 */

import { ProposalMilestone, MilestonePresets, MilestoneUrgency, Opportunity } from "../types";

export const DEFAULT_MILESTONE_PRESETS: MilestonePresets = {
  smeInputsOffsetDays: 5,
  firstDraftOffsetDays: 4,
  redTeamOffsetDays: 2,
  finalSignoffOffsetDays: 1,
  defaultTimezone: "America/Chicago",
};

/**
 * Checks whether a given Date is a Saturday (6) or Sunday (0).
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Adds or subtracts business days (Mon-Fri) from a given date string (YYYY-MM-DD).
 * Negative offsetDays steps backward in time, skipping weekends.
 */
export function addBusinessDays(startDateStr: string, offsetDays: number): string {
  const [year = 2026, month = 1, day = 1] = startDateStr.split("-").map(Number);
  const current = new Date(year, month - 1, day, 12, 0, 0); // Noon to avoid DST edge cases

  let remaining = Math.abs(offsetDays);
  const direction = offsetDays >= 0 ? 1 : -1;

  while (remaining > 0) {
    current.setDate(current.getDate() + direction);
    if (!isWeekend(current)) {
      remaining -= 1;
    }
  }

  // If the initial date landed on a weekend and offset was 0, roll to Friday or Monday
  if (offsetDays === 0 && isWeekend(current)) {
    while (isWeekend(current)) {
      current.setDate(current.getDate() - 1);
    }
  }

  const y = current.getFullYear();
  const m = String(current.getMonth() + 1).padStart(2, "0");
  const d = String(current.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Calculates number of business days between two dates (inclusive of start, exclusive of end).
 */
export function countBusinessDays(startDateStr: string, endDateStr: string): number {
  if (startDateStr >= endDateStr) return 0;
  const [sy = 2026, sm = 1, sd = 1] = startDateStr.split("-").map(Number);
  const [ey = 2026, em = 1, ed = 1] = endDateStr.split("-").map(Number);

  const cur = new Date(sy, sm - 1, sd, 12, 0, 0);
  const end = new Date(ey, em - 1, ed, 12, 0, 0);

  let count = 0;
  while (cur < end) {
    if (!isWeekend(cur)) {
      count++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * Derives milestone urgency at render time relative to the current time.
 */
export function getMilestoneUrgency(
  milestone: Pick<ProposalMilestone, "status" | "targetDate" | "targetTime" | "timezone">,
  now: Date = new Date(),
): MilestoneUrgency {
  if (milestone.status === "completed") {
    return "completed";
  }

  const timeStr = milestone.targetTime || "23:59";
  const [year = 2026, month = 1, day = 1] = milestone.targetDate.split("-").map(Number);
  const [hours = 0, minutes = 0] = timeStr.split(":").map(Number);
  const target = new Date(year, month - 1, day, hours, minutes, 0);

  const diffMs = target.getTime() - now.getTime();
  if (diffMs < 0) {
    return "overdue";
  }

  const hoursRemaining = diffMs / (1000 * 60 * 60);
  if (hoursRemaining <= 48) {
    return "due_soon";
  }

  return "upcoming";
}

export interface CountdownResult {
  days: number;
  hours: number;
  minutes: number;
  totalHours: number;
  isPast: boolean;
  label: string;
}

/**
 * Formats countdown time remaining until target date and time.
 */
export function formatCountdown(
  targetDate: string,
  targetTime?: string,
  _timezone: string = "America/Chicago",
  now: Date = new Date(),
): CountdownResult {
  if (!targetDate) {
    return { days: 0, hours: 0, minutes: 0, totalHours: 0, isPast: true, label: "No date set" };
  }

  const timeStr = targetTime || "17:00";
  const [year = 2026, month = 1, day = 1] = targetDate.split("-").map(Number);
  const [hours = 0, minutes = 0] = timeStr.split(":").map(Number);
  const target = new Date(year, month - 1, day, hours, minutes, 0);

  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      totalHours: 0,
      isPast: true,
      label: "Deadline passed",
    };
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const remHours = totalHours % 24;
  const remMinutes = totalMinutes % 60;

  let label: string;
  if (days > 0) {
    label = `${days}d ${remHours}h remaining`;
  } else if (remHours > 0) {
    label = `${remHours}h ${remMinutes}m remaining`;
  } else {
    label = `${remMinutes}m remaining`;
  }

  return {
    days,
    hours: remHours,
    minutes: remMinutes,
    totalHours,
    isPast: false,
    label,
  };
}

/**
 * Computes suggested response milestones using backward-scheduling from the proposal due date.
 */
export function computeAutoMilestones(
  opportunity: Opportunity,
  presets: Partial<MilestonePresets> = {},
  now: Date = new Date(),
): ProposalMilestone[] {
  if (!opportunity.dueDate) return [];

  const config: MilestonePresets = {
    ...DEFAULT_MILESTONE_PRESETS,
    ...presets,
  };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const nowIso = now.toISOString();
  const tz = config.defaultTimezone;
  const results: ProposalMilestone[] = [];

  // 1. Kickoff Milestone
  results.push({
    id: "auto_kickoff",
    opportunityId: opportunity.id,
    title: "Proposal Kickoff & Section Assignment",
    description: "Confirm Go decision, review requirements matrix, and assign section leads.",
    targetDate: todayStr,
    targetTime: "10:00",
    timezone: tz,
    category: "internal_gate",
    status: "pending",
    autoKey: "kickoff",
    humanEdited: false,
    createdAt: nowIso,
    updatedAt: nowIso,
  });

  // 2. Vendor Clarification Questions (if questionsDueDate is set)
  if (opportunity.questionsDueDate) {
    results.push({
      id: "auto_questions_due",
      opportunityId: opportunity.id,
      title: "Vendor Clarification Questions Cutoff",
      description: "Final deadline to submit questions to the client procurement officer.",
      targetDate: opportunity.questionsDueDate,
      targetTime: "17:00",
      timezone: tz,
      category: "external_deadline",
      status: "pending",
      autoKey: "questions_due",
      humanEdited: false,
      createdAt: nowIso,
      updatedAt: nowIso,
    });
  }

  // Check available business days between today and dueDate
  const availableBdays = countBusinessDays(todayStr, opportunity.dueDate);
  const isCompressed = availableBdays < 5;

  // Helper for internal gates
  const computeGateDate = (offset: number): string => {
    const calculated = addBusinessDays(opportunity.dueDate, -offset);
    if (calculated <= todayStr) {
      return todayStr;
    }
    return calculated;
  };

  // 3. SME Draft Inputs Due
  results.push({
    id: "auto_sme_inputs",
    opportunityId: opportunity.id,
    title: "SME & Technical Inputs Due",
    description: "Subject Matter Experts provide specific language, metrics, and data.",
    targetDate: computeGateDate(config.smeInputsOffsetDays),
    targetTime: "17:00",
    timezone: tz,
    category: "internal_gate",
    status: "pending",
    autoKey: "sme_inputs",
    humanEdited: false,
    compressed: isCompressed,
    createdAt: nowIso,
    updatedAt: nowIso,
  });

  // 4. First Complete Draft Review
  results.push({
    id: "auto_first_draft",
    opportunityId: opportunity.id,
    title: "First Complete Proposal Draft",
    description:
      "All requirements answered; initial compliance, completeness, and word count check.",
    targetDate: computeGateDate(config.firstDraftOffsetDays),
    targetTime: "17:00",
    timezone: tz,
    category: "internal_gate",
    status: "pending",
    autoKey: "first_draft",
    humanEdited: false,
    compressed: isCompressed,
    createdAt: nowIso,
    updatedAt: nowIso,
  });

  // 5. Evaluator Red Team Gate
  results.push({
    id: "auto_red_team",
    opportunityId: opportunity.id,
    title: "Evaluator Red Team & Writing Coach Gate",
    description: "Simulate procurement evaluation scoring, verify unsupported claims, and tone.",
    targetDate: computeGateDate(config.redTeamOffsetDays),
    targetTime: "17:00",
    timezone: tz,
    category: "internal_gate",
    status: "pending",
    autoKey: "red_team",
    humanEdited: false,
    compressed: isCompressed,
    createdAt: nowIso,
    updatedAt: nowIso,
  });

  // 6. Final Sign-Off & Packaging
  results.push({
    id: "auto_final_signoff",
    opportunityId: opportunity.id,
    title: "Final Executive Sign-Off & Package Assembly",
    description: "Verify mandatory forms, certifications, pricing sheet, and narrative format.",
    targetDate: computeGateDate(config.finalSignoffOffsetDays),
    targetTime: "15:00",
    timezone: tz,
    category: "internal_gate",
    status: "pending",
    autoKey: "final_signoff",
    humanEdited: false,
    compressed: isCompressed,
    createdAt: nowIso,
    updatedAt: nowIso,
  });

  // 7. Final Submission Deadline
  results.push({
    id: "auto_submission",
    opportunityId: opportunity.id,
    title: "Final Proposal Submission Deadline",
    description: `Official submission deadline to ${opportunity.client}.`,
    targetDate: opportunity.dueDate,
    targetTime: "15:00",
    timezone: tz,
    category: "external_deadline",
    status: "pending",
    autoKey: "submission",
    humanEdited: false,
    createdAt: nowIso,
    updatedAt: nowIso,
  });

  // Sort chronologically
  return results.sort((a, b) => {
    const cmp = a.targetDate.localeCompare(b.targetDate);
    if (cmp !== 0) return cmp;
    return (a.targetTime || "00:00").localeCompare(b.targetTime || "00:00");
  });
}

/**
 * Escapes characters for RFC 5545 iCalendar values.
 */
function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Formats a Date or date string to UTC compact format: YYYYMMDDTHHmmssZ
 */
function toIcsUtc(dateStr: string, timeStr?: string): string {
  const [year = 2026, month = 1, day = 1] = dateStr.split("-").map(Number);
  const [hours = 12, minutes = 0] = (timeStr || "12:00").split(":").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
}

/**
 * Builds RFC 5545 compliant .ics calendar content with CRLF line endings.
 */
export function buildIcs(
  milestones: ProposalMilestone[],
  opportunityTitle: string,
  clientName: string,
): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Multilingual Connections//MLC Proposal HQ//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];

  const nowUtc = toIcsUtc(new Date().toISOString().slice(0, 10), "12:00");

  for (const m of milestones) {
    lines.push("BEGIN:VEVENT");
    lines.push(`UID:${m.id}-${m.opportunityId}@mlc-proposal-hq`);
    lines.push(`DTSTAMP:${nowUtc}`);

    if (m.targetTime) {
      const dtStart = toIcsUtc(m.targetDate, m.targetTime);
      // End 1 hour later
      const [h = 0, min = 0] = m.targetTime.split(":").map(Number);
      const endH = String((h + 1) % 24).padStart(2, "0");
      const dtEnd = toIcsUtc(m.targetDate, `${endH}:${String(min).padStart(2, "0")}`);
      lines.push(`DTSTART:${dtStart}`);
      lines.push(`DTEND:${dtEnd}`);
    } else {
      const compactDate = m.targetDate.replace(/-/g, "");
      lines.push(`DTSTART;VALUE=DATE:${compactDate}`);
      lines.push(`DTEND;VALUE=DATE:${compactDate}`);
    }

    const summary = `[MLC RFP] ${m.title} - ${clientName}`;
    lines.push(`SUMMARY:${escapeIcsText(summary)}`);

    const desc = `${m.description || m.title}\n\nOpportunity: ${opportunityTitle}\nClient: ${clientName}\nStatus: ${m.status}`;
    lines.push(`DESCRIPTION:${escapeIcsText(desc)}`);

    // Add alarm 24h prior for external deadlines
    if (m.category === "external_deadline") {
      lines.push("BEGIN:VALARM");
      lines.push("TRIGGER:-P1D");
      lines.push("ACTION:DISPLAY");
      lines.push(`DESCRIPTION:${escapeIcsText(summary)}`);
      lines.push("END:VALARM");
    }

    lines.push("END:VEVENT");
  }

  lines.push("END:VCALENDAR");

  // RFC 5545 requires CRLF line endings
  return lines.join("\r\n") + "\r\n";
}

/**
 * Builds direct Google Calendar web event creation URL.
 */
export function buildGoogleCalendarUrl(
  milestone: ProposalMilestone,
  opportunityTitle: string,
  clientName: string,
): string {
  const title = encodeURIComponent(`[MLC RFP] ${milestone.title} - ${clientName}`);
  const details = encodeURIComponent(
    `${milestone.description || milestone.title}\n\nOpportunity: ${opportunityTitle}\nClient: ${clientName}`,
  );

  let datesParam: string;
  if (milestone.targetTime) {
    const compactDate = milestone.targetDate.replace(/-/g, "");
    const [h = 0, m = 0] = milestone.targetTime.split(":").map(Number);
    const startStr = `${compactDate}T${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
    const endH = (h + 1) % 24;
    const endStr = `${compactDate}T${String(endH).padStart(2, "0")}${String(m).padStart(2, "0")}00`;
    datesParam = `${startStr}/${endStr}`;
  } else {
    const compactDate = milestone.targetDate.replace(/-/g, "");
    datesParam = `${compactDate}/${compactDate}`;
  }

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${datesParam}`;
}
